import type AutodeskForgeAdaptor from "./autodesk_forge_adaptor";

/**
 * Loading a BIM is the heaviest thing the Explorer does, so when the user goes to another page of the project
 * its viewer is parked here, still loaded, instead of shut down. Coming back to the same model takes it back.
 * Leaving the project, or opening a different model, shuts it down.
 */
type ParkedViewer = { viewer: AutodeskForgeAdaptor, svfUrl: string, projectId: string };

let parked: ParkedViewer = null;
let holder: HTMLDivElement = null;

export function parkViewer(viewer: AutodeskForgeAdaptor, svfUrl: string, projectId: string) {
  disposeParkedViewer();
  if (holder == null) {
    holder = document.createElement("div");
    holder.className = "ParkedViewer";
    // Off screen rather than display: none, so the viewer keeps a size and its WebGL context.
    holder.style.cssText = "position: fixed; left: -10000px; top: 0; width: 1px; height: 1px; overflow: hidden; visibility: hidden;";
    document.body.appendChild(holder);
  }
  // Only the viewer's own element moves; the React-rendered container it sat in is unmounting.
  holder.appendChild(viewer.autodeskViewer.container);
  parked = { viewer, svfUrl, projectId };
}

/** The parked viewer, moved into the container, when it has this model loaded. */
export function takeParkedViewer(svfUrl: string, projectId: string, container: HTMLElement): AutodeskForgeAdaptor {
  if (parked == null) {
    return null;
  }
  if (parked.svfUrl !== svfUrl || parked.projectId !== projectId) {
    disposeParkedViewer();
    return null;
  }
  const { viewer } = parked;
  parked = null;
  container.appendChild(viewer.autodeskViewer.container);
  // @ts-ignore clientContainer (the element the viewer was created in) isn't in the typings
  viewer.autodeskViewer.clientContainer = container;
  viewer.resize();
  return viewer;
}

/** Shuts the parked viewer down unless it belongs to this project. */
export function disposeParkedViewerOutsideProject(projectId: string) {
  if (parked != null && parked.projectId !== projectId) {
    disposeParkedViewer();
  }
}

export function disposeParkedViewer() {
  if (parked != null) {
    const { viewer } = parked;
    parked = null;
    viewer.shutdown();
  }
}

export function hasParkedViewer() {
  return parked != null;
}
